/* eslint-disable @next/next/no-img-element */
import { Typography } from '@atlas-metrics/design-system';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Block, BLOCKS, Document, Inline } from '@contentful/rich-text-types';
import React, { PropsWithChildren, ReactNode } from 'react';

import clsxm from '@/lib/clsxm';

import ElevatedCard from '@/components/ui/ElevatedCard';
import Icon from '@/components/ui/Icon';

export interface IGalleryItemProps {
  title: string;
  description: Document;
  imageSrc: string;
  smallImageSrc: string | null;
  viewMoreLink?: string;
  className?: string;
}

const GalleryItem: React.FC<IGalleryItemProps> = ({
  className,
  title,
  description,
  imageSrc,
  smallImageSrc,
  viewMoreLink,
}) => {
  const BlockContent: React.FC<PropsWithChildren> = ({ children }) => (
    <span className='mb-3 block last:mb-0'>{children}</span>
  );

  const options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: Block | Inline, children: ReactNode) => (
        <BlockContent>{children}</BlockContent>
      ),
    },
  };

  return (
    <ElevatedCard
      className={clsxm(
        'flex flex-col overflow-hidden webS:!flex-row',
        className
      )}
    >
      {smallImageSrc && (
        <div
          className={clsxm(
            'h-[240px] w-[100%] bg-cover bg-center bg-no-repeat webS:hidden'
          )}
          style={{
            backgroundImage: `url('${smallImageSrc}')`,
          }}
        />
      )}
      <div
        className={clsxm(
          // smallImageSrc is optional
          smallImageSrc
            ? 'hidden h-auto w-[240px] bg-cover bg-center  bg-no-repeat webS:!block'
            : 'h-[240px] w-[100%] bg-cover bg-center bg-no-repeat webS:h-auto webS:w-[240px]'
        )}
        style={{
          backgroundImage: `url('${imageSrc}')`,
        }}
      />
      <div className='flex flex-col gap-6 p-4 webS:w-[calc(100%-240px)]'>
        <Typography variant='body-m' bold>
          {title}
        </Typography>
        <span className='text-s font-medium tracking-wide text-ds-grey-700'>
          {documentToReactComponents(description, options)}
        </span>
        {viewMoreLink && (
          <a href={viewMoreLink} target='_blank' rel='noreferrer'>
            <Typography
              variant='body-s'
              className='flex items-center gap-2 text-ds-purple-600'
            >
              View More
              <Icon
                name='icon-external-link'
                className='h-3 w-3 text-ds-purple-600'
              />
            </Typography>
          </a>
        )}
      </div>
    </ElevatedCard>
  );
};

export default GalleryItem;
